import { Trans, useLingui } from "@lingui/react/macro";

import { KIND_TITLES } from "@/features/drills";

import type { DashboardDrillKind } from "../../model/TeamDashboard";
import { BarList } from "./BarList";
import { DashboardCard } from "./DashboardCard";

type DrillKindsProps = {
  kinds: DashboardDrillKind[];
  answers: number;
  seriesStarted: number;
  seriesFinished: number;
};

// Средний результат ответа: верно — 1, почти — ½, мимо — 0.
const score = (kind: DashboardDrillKind): number => {
  const total = kind.right + kind.close + kind.wrong;
  return total === 0 ? 0 : (kind.right + kind.close / 2) / total;
};

// Ниже этого результата задача, похоже, слишком трудная или непонятная — полоса красная.
const HARD_SCORE = 0.45;

// «Тренировки по типам»: средний результат и число ответов по каждому типу задачи, сверху
// самые трудные; внизу — ответы и серии за период.
export const DrillKinds = ({ kinds, answers, seriesStarted, seriesFinished }: DrillKindsProps) => {
  const { i18n } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const percent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 0,
  });
  const rows = [...kinds]
    .sort((a, b) => score(a) - score(b))
    .map((kind) => {
      const value = score(kind);
      return {
        key: kind.kind,
        label: i18n._(KIND_TITLES[kind.kind]),
        value,
        valueText: percent.format(value),
        tone: value < HARD_SCORE ? ("bad" as const) : undefined,
        extra: number.format(kind.right + kind.close + kind.wrong),
      };
    });
  const answered = number.format(answers);
  const started = number.format(seriesStarted);
  const finished = number.format(seriesFinished);

  return (
    <DashboardCard
      title={<Trans>Drills by type</Trans>}
      description={
        <Trans>Average result: right — 100 %, close — 50 %. The hardest are on top</Trans>
      }
    >
      {rows.length === 0 ? (
        <p className="text-[13px] text-muted">
          <Trans>No drill answers in this period.</Trans>
        </p>
      ) : (
        <>
          <div
            aria-hidden="true"
            className="grid grid-cols-[minmax(0,min(170px,40%))_minmax(0,1fr)_44px_52px] gap-2.5 text-xs text-subtle"
          >
            <span />
            <span />
            <span className="text-right">
              <Trans>result</Trans>
            </span>
            <span className="text-right">
              <Trans>answers</Trans>
            </span>
          </div>
          <BarList rows={rows} labelWidth="wide" data-list="drill-kinds" />
        </>
      )}
      <p className="border-t border-border/60 pt-2.5 text-[13px] text-muted">
        <Trans>
          Training answers: <strong className="text-foreground">{answered}</strong>; series started:{" "}
          <strong className="text-foreground">{started}</strong>, finished:{" "}
          <strong className="text-foreground">{finished}</strong>.
        </Trans>
      </p>
    </DashboardCard>
  );
};
